/**
 * A gaveta: o esperado agora, a conta que o produziu, e as duas ações que o
 * mexem.
 *
 * A conta aparece ABERTA — abertura, vendas, suprimentos e sangrias, em linhas
 * separadas — e não só o total. É a diferença entre o operador conferir e o
 * operador acreditar: com as parcelas à vista, uma sangria esquecida salta aos
 * olhos antes do fechamento, e não depois dele.
 */
import { Icone, type NomeIcone } from '../../../componentes/Icone'
import { formatarMoeda, type Centavos } from '../../../plataforma/dinheiro'
import type { Turno } from '../../../plataforma/caixa'

export function PainelGaveta({
  turno,
  podeMovimentar,
  aoSangrar,
  aoSuprir,
}: {
  turno: Turno
  podeMovimentar: boolean
  aoSangrar: () => void
  aoSuprir: () => void
}) {
  return (
    <section className="rounded-sistema border border-borda bg-superficie p-5 shadow-[var(--shadow-cartao)]">
      <div className="mb-4">
        <h2 className="font-titulo text-base font-semibold text-tinta">Gaveta</h2>
        <p className="mt-[3px] text-[12.5px] text-texto-suave">
          Só dinheiro. Cartão e PIX não passam por aqui.
        </p>
      </div>

      <div className="tabular font-titulo text-[32px] font-bold text-tinta">
        {formatarMoeda(turno.gaveta_centavos)}
      </div>

      <div className="mt-4 border-t border-divisoria pt-3">
        <Parcela rotulo="Abertura" valor={turno.abertura_centavos} entra />
        <Parcela rotulo="Vendas em dinheiro" valor={turno.vendas_dinheiro_centavos} entra />
        <Parcela rotulo="Suprimentos" valor={turno.suprimentos_centavos} entra />
        <Parcela rotulo="Sangrias" valor={turno.sangrias_centavos} entra={false} />
        {turno.devolucoes_centavos > 0 && (
          <Parcela rotulo="Devoluções" valor={turno.devolucoes_centavos} entra={false} />
        )}
      </div>

      {turno.status === 'aberto' && (
        <div className="mt-4 flex flex-col gap-2.5 border-t border-divisoria pt-4">
          <BotaoDeMovimento
            icone="trending_down"
            rotulo="Sangria"
            habilitado={podeMovimentar}
            onClick={aoSangrar}
          />
          <BotaoDeMovimento
            icone="trending_up"
            rotulo="Suprimento"
            habilitado={podeMovimentar}
            onClick={aoSuprir}
          />
          {!podeMovimentar && (
            <p className="text-[12px] leading-[1.45] text-texto-fraco">
              Seu papel não movimenta a gaveta. Peça a um gerente.
            </p>
          )}
        </div>
      )}
    </section>
  )
}

function Parcela({
  rotulo,
  valor,
  entra,
}: {
  rotulo: string
  valor: Centavos
  entra: boolean
}) {
  return (
    <div className="flex items-baseline justify-between py-1">
      <span className="text-[12.5px] text-texto-suave">{rotulo}</span>
      <span className="tabular text-[13px] font-medium text-texto">
        {entra ? '+' : '−'} {formatarMoeda(valor)}
      </span>
    </div>
  )
}

function BotaoDeMovimento({
  icone,
  rotulo,
  habilitado,
  onClick,
}: {
  icone: NomeIcone
  rotulo: string
  habilitado: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={!habilitado}
      onClick={onClick}
      className={`flex h-9 w-full items-center justify-center gap-2 rounded-sistema border text-[13px] font-semibold ${
        habilitado
          ? 'border-borda bg-white text-marca hover:bg-marca-fundo-2'
          : 'border-borda bg-divisoria text-texto-fraco'
      }`}
    >
      <Icone nome={icone} tamanho={17} />
      {rotulo}
    </button>
  )
}
